(() => {
  const HOST_SELECTOR = 'sc-compare-dialog'; // change if your host tag differs
  const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

  const BADGE_WRAP_ATTR = 'data-sw-compare-badges';
  const BADGE_HANDLE_ATTR = 'data-sw-compare-handle';
  const BADGE_KEYS_ATTR = 'data-sw-compare-badges-keys';

  const BADGE_ORDER = ['bogo', 'rewards_2x', 'is_new', 'new_improved', 'new_lower_price'];
  const BADGE_LABEL = {
    bogo: 'BOGO',
    rewards_2x: '2X Rewards',
    is_new: 'New',
    new_improved: 'New & Improved',
    new_lower_price: 'New Lower Price',
  };

  const formatRange = (r) => {
    const a = r?.from?.amount;
    const b = r?.to?.amount;
    if (typeof a !== 'number') return null;
    if (typeof b !== 'number' || a === b) return fmt.format(a);
    return `${fmt.format(a)} - ${fmt.format(b)}`;
  };

  /*
    The worker computes pricePerServingCents from the SALE price and does not return the
    serving count, so under suppression the figure would let a shopper multiply the hidden
    price back out (ECOMDEV-903). Scale it to the displayed compare-at price instead.
    This is an approximation — the worker's own rounding is lossy, so it can sit a cent or
    two off. An exact figure needs the worker to return `totalServings`, which it already
    computes internally but does not expose.
  */
  const formatPricePerServing = (card, suppressed) => {
    const cents = card?.pricePerServingCents;
    if (typeof cents !== 'number') return null;

    let displayCents = cents;
    if (suppressed) {
      const ps = card?.priceSet;
      const sale = ps?.price?.to?.amount;
      const compare = ps?.originalPrice?.to?.amount;
      if (typeof sale === 'number' && sale > 0 && typeof compare === 'number' && compare > 0) {
        displayCents = Math.round(cents * (compare / sale));
      } else {
        return null;
      }
    }
    return `(${fmt.format(displayCents / 100)}/serving)`;
  };

  const normalizePct = (v) => {
    if (typeof v !== 'number' || Number.isNaN(v)) return null;
    // handle both 25 and 0.25 shapes
    const n = v <= 1 ? v * 100 : v;
    return Math.round(n);
  };

  const findHost = () => {
    const direct = document.querySelector(HOST_SELECTOR);
    if (direct?.shadowRoot?.querySelector('dialog.compare-dialog')) return direct;

    // tiny fallback scan for open shadowRoots containing the dialog
    for (const el of document.querySelectorAll('*')) {
      const sr = el.shadowRoot;
      if (sr?.querySelector?.('dialog.compare-dialog')) return el;
    }
    return null;
  };

  const priceStyleApplicator = (el, kind) => {
    switch (kind) {
      case 'regular':
        break;
      case 'promo':
        el.style.color = '#b93e09';
        return el;
      case 'crossout':
        el.style.color = '#2b2b2b';
        el.style.fontSize = '0.75rem';
        el.style.lineHeight = '1rem';
        el.style.textDecorationLine = 'line-through';
        el.style.textDecorationThickness = '2px';
        return el;
      case 'sub':
        el.style.color = '#b93e09';
        el.style.fontSize = '0.75rem';
        el.style.lineHeight = '1rem';
        return el;
      case 'perServing':
        el.style.color = '#2b2b2b';
        el.style.fontSize = '0.75rem';
        el.style.lineHeight = '1rem';
        return el;
      default:
        break;
    }
  };

  const makeBadgeStackTopLeft = (badgeKeys, handle) => {
    const wrap = document.createElement('div');
    wrap.setAttribute(BADGE_WRAP_ATTR, 'true');
    wrap.setAttribute(BADGE_HANDLE_ATTR, handle);
    wrap.setAttribute(BADGE_KEYS_ATTR, badgeKeys.join(','));

    wrap.style.position = 'absolute';
    wrap.style.top = '8px';
    wrap.style.left = '8px';
    wrap.style.zIndex = '99';
    wrap.style.display = 'flex';
    wrap.style.flexDirection = 'column';
    wrap.style.gap = '8px';
    wrap.style.alignItems = 'flex-start';

    for (const key of badgeKeys) {
      const badge = document.createElement('div');
      badge.textContent = BADGE_LABEL[key] || key;

      badge.style.display = 'flex';
      badge.style.alignItems = 'center';
      badge.style.justifyContent = 'center';
      badge.style.textAlign = 'center';

      badge.style.color = '#2b2b2b';
      badge.style.backgroundColor = '#f1f1f1';

      badge.style.fontSize = '12px';
      badge.style.fontWeight = '500';
      badge.style.textTransform = 'none';

      badge.style.paddingBlock = '8px';
      badge.style.paddingInline = '16px';
      badge.style.borderRadius = '4px';

      wrap.appendChild(badge);
    }

    return wrap;
  };

  const patchBadgesForProduct = (product, handle, card) => {
    // only accept worker tokens, enforce worker order, dedupe
    const raw = Array.isArray(card?.badges) ? card.badges : [];
    const keys = Array.from(new Set(raw))
      .filter((k) => Boolean(BADGE_LABEL[k]))
      .sort((a, b) => BADGE_ORDER.indexOf(a) - BADGE_ORDER.indexOf(b));

    // anchor: the image wrapper container in your markup
    const anchor = product.querySelector('.relative') || product;

    // ensure anchor can host absolute-positioned badge stack
    if (getComputedStyle(anchor).position === 'static') {
      anchor.style.position = 'relative';
    }

    const existing = anchor.querySelector(`[${BADGE_WRAP_ATTR}="true"]`);

    if (!keys.length) {
      if (existing) existing.remove();
      return;
    }

    const nextSig = keys.join(',');
    if (existing && existing.getAttribute(BADGE_KEYS_ATTR) === nextSig) return;

    if (existing) existing.remove();
    anchor.appendChild(makeBadgeStackTopLeft(keys, handle));
  };

  const getRelationshipText = (card) => {
    const parts = [];

    if ((card.flavorCount ?? 0) > 0) {
      parts.push(`${card.flavorCount} Flavors`);
    }

    if ((card.sizeCount ?? 0) > 0) {
      parts.push(`${card.sizeCount} Sizes`);
    }

    return parts.join(' · ');
  };

  const patchProductParentChild = (card) => {
    const relationship = document.createElement('div');
    relationship.style.fontWeight = '700';
    relationship.style.fontSize = '0.875rem';
    relationship.style.lineHeight = '1.25rem';
    relationship.textContent = getRelationshipText(card);
    return relationship;
  };

  const patchPotencyNoInfo = (sr) => {
    sr.querySelectorAll('tr.row.row--attribute').forEach((row) => {
      const header = row.querySelector('th .attribute-name');
      if (!header) return;
      if (header.textContent.trim() !== 'Potency') return;

      row
        .querySelectorAll('.attribute-value span[aria-label="No information provided"]')
        .forEach((span) => {
          if (span.dataset.swPotencyPatched === 'true') return;

          span.textContent = 'See supplement facts';
          span.setAttribute('aria-label', 'See supplement facts');
          span.dataset.swPotencyPatched = 'true';
        });
    });
  };

  const patch = (sr) => {
    patchPotencyNoInfo(sr);
    const get = window.SWCompareMeta?.get;
    if (typeof get !== 'function') return;

    sr.querySelectorAll('sc-product[product-handle]').forEach((product) => {
      const handle = (product.getAttribute('product-handle') || '').trim();
      if (!handle) return;

      const priceEl = product.querySelector('.product__price');
      if (!priceEl) return;

      const card = get(handle);
      patchBadgesForProduct(product, handle, card);
      const ps = card?.priceSet;
      if (!ps) return;

      const now =
        formatRange(ps.price) ||
        (ps.kind === 'sale' || ps.kind === 'range' ? formatRange(ps.originalPrice) : null);
      if (!now) return;

      const was = ps.kind === 'sale' || ps.kind === 'range' ? formatRange(ps.originalPrice) : null;

      const pct = normalizePct(ps.savingPercentage);

      /*
        ECOMDEV-903. `Theme.offer_code` is the raw applied code and `Theme.price_offer_code`
        the approved one used for display; they differ ONLY when an unapproved code was
        substituted. In that case the sale price must not be advertised: show the
        compare-at figure alone and tell the shopper the discount lands in the cart.
        Approved codes, and the setting being off, leave every branch below untouched.
      */
      const isSuppressed =
        Boolean(Theme?.hide_offer_price) &&
        Boolean(Theme?.price_offer_code) &&
        Theme.offer_code !== Theme.price_offer_code &&
        (ps.kind === 'sale' || ps.kind === 'range') &&
        Boolean(was);

      // Computed after `isSuppressed` — it needs to know whether to rebase to compare-at.
      const perServingText = formatPricePerServing(card, isSuppressed);

      // signature so we don't re-render the same thing repeatedly
      const sig = JSON.stringify({ k: ps.kind, now, was, pct, perServingText, s: isSuppressed });
      if (priceEl.dataset.swOverriddenSig === sig) return;

      // clear + make multi-line possible
      priceEl.textContent = '';
      priceEl.style.display = 'inline-block';
      priceEl.style.whiteSpace = 'normal';

      const mainLine = document.createElement('div');
      mainLine.style.fontWeight = '700';
      mainLine.style.fontSize = '0.875rem';
      mainLine.style.lineHeight = '1.25rem';

      if (card.hasChildren) {
        priceEl.appendChild(patchProductParentChild(card));
      }

      // ---- regular ----
      if (ps.kind === 'regular') {
        mainLine.textContent = now;
        priceEl.appendChild(mainLine);
      }

      // ---- bogo ----
      else if (ps.kind === 'bogo') {
        // "2 for $8.29"
        mainLine.className = 'bogo offer';
        priceStyleApplicator(mainLine, 'promo');
        mainLine.textContent = `2 for ${now}`;
        priceEl.appendChild(mainLine);

        // "2 for 1 offer"
        const sub = document.createElement('div');
        sub.className = '2 for 1';
        priceStyleApplicator(sub, 'sub');
        sub.textContent = '2 for 1 offer';
        priceEl.appendChild(sub);
      }

      // ---- suppressed: compare-at alone + "Save X% in cart" (ECOMDEV-903) ----
      else if (isSuppressed) {
        mainLine.textContent = was;
        priceStyleApplicator(mainLine, 'regular');
        priceEl.appendChild(mainLine);

        if (pct !== null) {
          const sub = document.createElement('div');
          priceStyleApplicator(sub, 'sub');
          sub.textContent = `Save ${pct}% in cart`;
          priceEl.appendChild(sub);
        }
      }

      // ---- sale / range ----
      else {
        // first line: "$X" (or "$X - $Y") + " $was" struck
        mainLine.textContent = now;
        const saleContainer = document.createElement('div'); // promo price
        saleContainer.style.display = 'flex';
        saleContainer.style.alignItems = 'baseline';
        saleContainer.style.gap = '4px';

        priceStyleApplicator(mainLine, pct ? 'promo' : 'regular');

        saleContainer.appendChild(mainLine);
        if (pct) {
          const oldLine = document.createElement('span'); // original non-promo price
          priceStyleApplicator(oldLine, 'crossout');
          oldLine.textContent = was;
          saleContainer.appendChild(oldLine);
        }
        priceEl.appendChild(saleContainer);

        // second line: "% off" / "Up to % off"
        if (pct !== null) {
          const sub = document.createElement('div');
          priceStyleApplicator(sub, 'sub');
          sub.textContent = ps.kind === 'range' ? `Up to ${pct}% off` : `${pct}% off`;
          priceEl.appendChild(sub);
        }
      }

      if (perServingText) {
        const pps = document.createElement('div');
        priceStyleApplicator(pps, 'perServing');
        pps.textContent = perServingText;
        priceEl.appendChild(pps);
      }
      priceEl.dataset.swOverriddenSig = sig;
    });
  };

  const attach = (host) => {
    const sr = host.shadowRoot;
    if (!sr || sr.__swPriceOverrideAttached) return;
    sr.__swPriceOverrideAttached = true;

    patch(sr);

    new MutationObserver(() => patch(sr)).observe(sr, { subtree: true, childList: true });
    document.addEventListener('sw:compare:meta-change', () => patch(sr));
    document.addEventListener('sw:offer-code-change', () => patch(sr));
    document.addEventListener('sw:card:refresh', () => patch(sr));
  };

  const host = findHost();
  if (host) attach(host);

  const mo = new MutationObserver(() => {
    const h = findHost();
    if (h) {
      attach(h);
      mo.disconnect();
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
